import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  ChevronRight, 
  AlertCircle,
  ShieldCheck,
  ArrowRight
} from 'lucide-react';
import ScrollToTop from '../../components/Common/ScrollToTop';
import logoImage from '../../assets/logo.png';
import { apiService } from '../../services/apiService';
import { USE_BACKEND_API } from '../../config/apiConfig';
import { useToast } from '../../contexts/ToastContext';
import { GoogleLogin } from '@react-oauth/google';

const AdminLogin: React.FC = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showLegacyAuth, setShowLegacyAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { showSuccess } = useToast();

  useEffect(() => {
    const checkSession = async () => {
      if (!USE_BACKEND_API) return;
      try {
        const user = await apiService.getCurrentUser();
        // getCurrentUser returns null when no token is present — skip silently
        if (!user) return;
        if (user.role === 'admin' && user.admin_status === 'approved') {
          localStorage.setItem('adminAuth', 'true');
          navigate('/admin/dashboard');
        }
      } catch (err) {
        // Silent catch: network error or invalid token
      }
    };
    checkSession();
  }, [navigate]);

  const handleLegacyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      localStorage.removeItem('authToken');

      if (USE_BACKEND_API) {
        const adminEmail = credentials.username.includes('@')
          ? credentials.username
          : (process.env.REACT_APP_ADMIN_EMAIL || 'admin@sighttrails.com');

        try {
          const result = await apiService.login(adminEmail, credentials.password);

          if (result.user && result.user.role === 'admin') {
            localStorage.setItem('adminAuth', 'true');
            if (result.token) {
              localStorage.setItem('authToken', result.token);
            }
            navigate('/admin/dashboard');
            return;
          } else {
            setError('Access denied. Admin privileges required.');
            return;
          }
        } catch (apiError: any) {
          setError(apiError.message || 'Invalid credentials. Please try again.');
          return;
        }
      }

      const adminUsername = process.env.REACT_APP_ADMIN_USERNAME || 'admin';
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'safari2024';

      if (credentials.username === adminUsername && credentials.password === adminPassword) {
        localStorage.setItem('adminAuth', 'true');
        navigate('/admin/dashboard');
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    if (!credentialResponse?.credential) {
      setError('Google authentication failed.');
      return;
    }
    setIsLoading(true);
    try {
      const result = await apiService.googleLogin(credentialResponse.credential, true);
      if (result.user && result.user.role === 'admin') {
        localStorage.setItem('adminAuth', 'true');
        if (result.token) {
          localStorage.setItem('authToken', result.token);
        }
        showSuccess('Success', 'Logged in with Google');
        navigate('/admin/dashboard');
      } else {
        setError('Access denied. Admin privileges required.');
      }
    } catch (err: any) {
      setError(err.message || 'Google login error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled or failed.');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.1
      }
    }
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  } as const;

  return (
    <div className="min-h-screen w-full bg-[#F8FBFF] flex flex-col items-center justify-center p-6 font-sans selection:bg-primary-100 italic-shadows overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none overflow-hidden">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-md"
      >
        {/* Branding Context */}
        <motion.div variants={itemVariants} className="flex flex-col items-center mb-10">
          <button 
            onClick={() => navigate('/admin-access')}
            className="flex items-center text-xs font-bold text-primary-400 hover:text-primary-600 transition-colors uppercase tracking-widest group mb-6"
          >
            <ArrowRight className="w-3.5 h-3.5 mr-2 rotate-180 group-hover:-translate-x-1 transition-transform" />
            Control Hub
          </button>
          
          <div className="w-20 h-20 bg-white rounded-3xl shadow-lg border border-blue-50 p-4 flex items-center justify-center mb-6 overflow-hidden">
            <img
              src={logoImage}
              alt="SightTrails Logo"
              className="w-full h-full object-contain"
            />
          </div>
          
          <h1 className="text-3xl font-extrabold text-primary-900 tracking-tight font-serif mb-2">
            Admin Login
          </h1>
          <p className="text-neutral-400 text-sm font-medium">SightTrail East African Safaris</p>
        </motion.div>

        {/* Login Card */}
        <motion.div 
          variants={itemVariants}
          className="bg-white rounded-[32px] shadow-[0_32px_64px_-16px_rgba(42,126,206,0.12)] border border-blue-50 p-10 sm:p-12"
        >
          <div className="space-y-6">
            <AnimatePresence mode="wait">
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center bg-red-50/50 border border-red-100 text-red-600 px-4 py-3 rounded-2xl text-xs font-bold gap-3 overflow-hidden"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Google OAuth Section (Primary) */}
            <div className="flex items-center justify-center pt-2 pb-4">
              <div className="w-[250px] rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 bg-white flex items-center justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  width="250"
                  shape="pill"
                  theme="outline"
                  text="signin_with"
                />
              </div>
            </div>

            <div className="relative flex items-center py-4">
              <div className="flex-grow border-t border-neutral-100"></div>
              <button 
                type="button"
                onClick={() => setShowLegacyAuth(!showLegacyAuth)}
                className="flex-shrink-0 mx-4 text-xs font-bold text-neutral-400 hover:text-primary-600 transition-colors uppercase tracking-widest bg-white px-2"
              >
                Use Credentials
              </button>
              <div className="flex-grow border-t border-neutral-100"></div>
            </div>

            <AnimatePresence>
              {showLegacyAuth && (
                <motion.form 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  onSubmit={handleLegacyLogin} 
                  className="space-y-6 overflow-hidden"
                >
                  <div className="space-y-5 pt-2">
                    {/* Username Input */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-primary-900 uppercase tracking-wider ml-1">Username</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4.5 flex items-center pointer-events-none">
                          <User className="w-5 h-5 text-neutral-300 group-focus-within:text-primary-500 transition-colors" />
                        </div>
                        <input
                          name="username"
                          type="text"
                          required
                          value={credentials.username}
                          onChange={handleInputChange}
                          placeholder="Administrator ID"
                          className="w-full pl-12 pr-4 py-4 bg-neutral-50/50 border border-neutral-100 rounded-2xl text-primary-900 text-sm placeholder:text-neutral-300 focus:outline-none focus:bg-white focus:border-primary-400 focus:shadow-[0_0_0_4px_rgba(42,126,206,0.06)] transition-all"
                        />
                      </div>
                    </div>

                    {/* Password Input */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-xs font-bold text-primary-900 uppercase tracking-wider">Password</label>
                      </div>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4.5 flex items-center pointer-events-none">
                          <Lock className="w-5 h-5 text-neutral-300 group-focus-within:text-primary-500 transition-colors" />
                        </div>
                        <input
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          value={credentials.password}
                          onChange={handleInputChange}
                          placeholder="••••••••"
                          className="w-full pl-12 pr-12 py-4 bg-neutral-50/50 border border-neutral-100 rounded-2xl text-primary-900 text-sm placeholder:text-neutral-300 focus:outline-none focus:bg-white focus:border-primary-400 focus:shadow-[0_0_0_4px_rgba(42,126,206,0.06)] transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-300 hover:text-primary-500 transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    disabled={isLoading}
                    className="w-full py-4.5 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-bold text-lg shadow-xl shadow-primary-900/10 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Processing...' : 'Sign In'}
                    {!isLoading && <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Footer info */}
        <motion.div variants={itemVariants} className="mt-10 text-center">
          <div className="text-[10px] font-bold text-primary-200 uppercase tracking-[0.3em] mb-4">
            Authorized Personnel Only
          </div>
          <div className="flex items-center justify-center space-x-6 text-[10px] text-neutral-400 font-medium">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-green-500" />
              Verified Session
            </span>
            <span className="w-1 h-1 bg-neutral-200 rounded-full" />
            <span className="cursor-default hover:text-primary-600 transition-colors">Access Audit</span>
          </div>
        </motion.div>
      </motion.div>

      <ScrollToTop />
    </div>
  );
};

export default AdminLogin;
